import { useCallback, useEffect, useState } from "react";

import { resolveDatafields } from "../application/propertyResolutionService";

type UsePreviewDataParams = {
	graphic: Graphic;
	rawData: unknown | undefined;
	currentPropertyValues: PropertyValue;
};

type UsePreviewDataReturn = {
	previewData: PropertyValue | undefined;
	updatePreview: (params: { propertyId: string; value: string | PropertyValue[] }) => void;
	clearPreview: () => void;
	getInjectedValues: () => PropertyValue;
};

export const usePreviewData = (params: UsePreviewDataParams): UsePreviewDataReturn => {
	const { graphic, rawData, currentPropertyValues } = params;
	const [previewData, setPreviewData] = useState<PropertyValue | undefined>(undefined);

	const resolveAndSetPreview = useCallback(async () => {
		if (!rawData) {
			setPreviewData(undefined);
			return;
		}

		const resolvedResult = await resolveDatafields({
			properties: graphic.properties,
			data: rawData,
		});

		if (Object.keys(resolvedResult.errors).length > 0) {
			console.error("Property resolution errors:", resolvedResult.errors);
		}

		setPreviewData(resolvedResult.propertyValues);
	}, [rawData, graphic]);

	const updatePreview = useCallback(
		(updateParams: { propertyId: string; value: string | PropertyValue[] }) => {
			if (!previewData) {
				return;
			}

			setPreviewData({
				...previewData,
				[updateParams.propertyId]: updateParams.value,
			});
		},
		[previewData],
	);

	const clearPreview = useCallback(() => {
		setPreviewData(undefined);
	}, []);

	const getInjectedValues = useCallback((): PropertyValue => {
		if (!previewData) {
			return currentPropertyValues;
		}

		return {
			...currentPropertyValues,
			...previewData,
		};
	}, [previewData, currentPropertyValues]);

	// Resolve data when rawData changes
	useEffect(() => {
		if (rawData) {
			void resolveAndSetPreview();
		} else {
			setPreviewData(undefined);
		}
	}, [rawData, resolveAndSetPreview]);

	return {
		previewData,
		updatePreview,
		clearPreview,
		getInjectedValues,
	};
};
